import { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

// Ajustes dos gráficos (recharts) dos dashboards para tela de celular (< 600px).
// Uso: `const graficoResp = useGraficoResponsivo();` e espalhar nos
// componentes ANTES das props próprias do gráfico (que continuam valendo):
//   <AreaChart margin={graficoResp.margem} ...>
//   <CartesianGrid {...graficoResp.grade} strokeDasharray="3 3" />
//   <XAxis {...graficoResp.eixoX} dataKey="..." />
//   <YAxis {...graficoResp.eixoY} tickFormatter={...} />
//   <RechartsTooltip {...graficoResp.dica} />
//   <Legend {...graficoResp.legenda} />
//   <Pie {...graficoResp.pizza} ... />            (rosca/pizza com rótulo)
//   <Box sx={{ height: graficoResp.altura(360) }}> (altura da área do gráfico)
//   <Paper sx={{ p: graficoResp.respiro }}>      (respiro do cartão)
//   <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
// No celular: margens mínimas (o eixo Y padrão de 60px come 1/5 da largura),
// grade só horizontal, rótulos menores e espaçados sem sobrepor, dica que
// não escapa da tela, legenda compacta, cartão com menos respiro e os grupos
// de botões do título rolando de lado em vez de estourar o cartão. No
// computador devolve tudo vazio — o gráfico fica exatamente como era.
export const useGraficoResponsivo = () => {
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('sm'));

  return useMemo(() => {
    if (!celular) {
      return {
        celular,
        margem: undefined,
        grade: {},
        eixoX: {},
        eixoY: {},
        dica: {},
        legenda: {},
        pizza: {},
        grupoBotoes: {},
        respiro: 2,
        altura: (computador: number) => computador,
      };
    }
    return {
      celular,
      margem: { top: 8, right: 6, left: -6, bottom: 0 },
      grade: { vertical: false },
      eixoX: { tick: { fontSize: 10 }, interval: 'preserveStartEnd' as const, minTickGap: 12, tickMargin: 4 },
      eixoY: { tick: { fontSize: 10 }, width: 42 },
      dica: {
        allowEscapeViewBox: { x: false, y: false },
        wrapperStyle: { maxWidth: 'calc(100vw - 48px)', fontSize: 12, zIndex: 2 },
        contentStyle: { fontSize: 12, padding: '6px 8px' },
      },
      legenda: { iconSize: 8, wrapperStyle: { fontSize: 11, paddingTop: 4, lineHeight: '16px' } },
      pizza: { isAnimationActive: false },
      grupoBotoes: {
        maxWidth: '100%',
        overflowX: 'auto',
        '& .MuiButton-root': { px: 1, minWidth: 0, fontSize: '0.7rem', whiteSpace: 'nowrap' },
      },
      respiro: 1.5,
      // Celular: ~72% da altura do computador, nunca menos de 220px.
      altura: (computador: number) => Math.max(220, Math.round(computador * 0.72)),
    };
  }, [celular]);
};
